import ResponsiveImage from '../components/ResponsiveImage';
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

export default function ArticleMarketingVs() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="bg-[#f5f1ea] text-[#141414] overflow-x-hidden min-h-screen">
      <Navbar />
      
      {/* Article Hero */}
      <section className="pt-[160px] md:pt-[200px] pb-12 md:pb-20 px-6 md:px-12 lg:px-20 max-w-[1000px] mx-auto">
        <p className="text-[12px] uppercase tracking-[0.22em] text-[#141414]/55 mb-6">
          Marketing y Estrategia · Lectura 9 min
        </p>
        <h1 className="font-serif text-[36px] md:text-[48px] lg:text-[56px] leading-[1.05] tracking-[-0.02em] font-normal mb-8">
          Marketing Directo vs. Indirecto: ¿Por qué la persecución ya no funciona?
        </h1>
        <p className="text-[18px] md:text-[22px] leading-[1.6] text-[#141414]/70 max-w-[800px] mb-12">
          Dos filosofías de crecimiento completamente opuestas. Una te posiciona como un vendedor desesperado que quema su presupuesto intentando convencer a quien no quiere escuchar. La otra, construye un foso de autoridad infranqueable.
        </p>
        <div className="w-full aspect-[16/9] md:aspect-[21/9] overflow-hidden mb-16">
          <ResponsiveImage priority sizes="(min-width: 1120px) 1024px, calc(100vw - 48px)" 
            src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?q=80&w=1200&auto=format&fit=crop&sat=-100" 
            alt="Contraste entre marketing directo ruidoso y marketing indirecto elegante" 
            className="w-full h-full object-cover object-center"
          />
        </div>

        {/* Article Body */}
        <article className="prose prose-lg md:prose-xl max-w-[800px] font-sans text-[#141414]/80 leading-[1.8]">
          <p>
            Imagina que entras en una tienda de ropa exclusiva. Nada más cruzar la puerta, un dependiente corre hacia ti, te bloquea el paso, te pone una camisa en la cara y te grita: <strong>"¡COMPRA ESTA CAMISA AHORA, ÚLTIMAS TALLAS, 50% DE DESCUENTO SOLO HOY!"</strong>.
          </p>
          <p>
            ¿Qué haces? Te das la vuelta y sales huyendo. No vuelves nunca más. 
            Suena ridículo en el mundo físico, pero esta es, exactamente, la experiencia que miles de empresas ofrecen a sus clientes potenciales en el mundo digital todos los días. A esto lo llamamos <strong>Marketing Directo de Fuerza Bruta</strong>.
          </p>
          
          <h2 className="font-serif text-[28px] text-[#141414] mt-12 mb-6 tracking-[-0.01em]">La trampa del Marketing Directo y el Vendedor Desesperado</h2>
          <p>
            El marketing directo tradicional tiene un único objetivo: la transacción inmediata. Su lenguaje es el de la urgencia, el descuento y la presión. Utiliza correos electrónicos fríos no solicitados (spam), llamadas telefónicas inoportunas y anuncios en redes sociales que interrumpen tu navegación exigiéndote un clic.
          </p>
          <p>
            ¿Funcionaba hace diez años? Sí. Cuando el consumidor digital era ingenuo y había menos ruido, gritar más alto que los demás daba resultados. Pero hoy, el consumidor (y especialmente el comprador B2B o el cliente de servicios premium) ha desarrollado un escudo impenetrable. Tienen bloqueadores de anuncios instalados en sus navegadores y en su cerebro. <strong>Detectan a un vendedor a kilómetros de distancia.</strong>
          </p>
          <p>
            El coste oculto del marketing directo extremo no es solo el bajo retorno de inversión en publicidad (ROAS). El verdadero coste es <strong>el daño a tu marca</strong>. Cuando actúas como alguien desesperado por vender, automáticamente devalúas tu servicio. Le estás diciendo al mercado: "No soy el líder, no me sobran los clientes, por favor, cómprame".
          </p>

          <h2 className="font-serif text-[28px] text-[#141414] mt-12 mb-6 tracking-[-0.01em]">El paradigma del Marketing Indirecto (Inbound y Educación)</h2>
          <p>
            Volvamos al ejemplo de la tienda. Esta vez, entras en una boutique elegante. Huele bien, la iluminación es perfecta. Nadie te acosa. Mientras miras, ves un pequeño libro o folleto muy bien editado sobre "Cómo distinguir los tejidos italianos de alta costura". Te resulta interesante y lo hojeas. Cuando finalmente decides que quieres un traje, te acercas al dependiente, que te asesora como un sastre experto.
          </p>
          <p>
            Eso es <strong>Marketing Indirecto</strong>. No pide la venta de inmediato. Su objetivo es aportar tanto valor por adelantado que, cuando el cliente necesite comprar, tú seas la única opción lógica en su cabeza.
          </p>
          <p>
            En el mundo digital, el marketing indirecto se viste de <strong>Educación</strong>. Se apoya en estrategias como la <em>Publicidad Nativa</em> y el marketing de contenidos editoriales. En lugar de interrumpir al usuario con un banner de oferta, le ofreces un artículo en un medio de prestigio que analiza profundamente el problema que el cliente ni siquiera sabía cómo articular.
          </p>

          <blockquote className="border-l-4 border-[#141414] pl-6 my-10 font-serif text-[22px] md:text-[26px] leading-[1.4] text-[#141414]">
            "El marketing directo es pedir matrimonio en la primera cita. El marketing indirecto es convertirte en la persona con la que todo el mundo se quiere casar."
          </blockquote>

          <h2 className="font-serif text-[28px] text-[#141414] mt-12 mb-6 tracking-[-0.01em]">La asimetría de la información</h2>
          <p>
            En sectores de alto ticket (clínicas, despachos de abogados, agencias inmobiliarias, B2B), existe lo que los economistas llaman <em>asimetría de información</em>. Tú sabes mucho sobre tu servicio, y el cliente sabe muy poco. Tiene miedo a equivocarse porque la decisión es cara e importante.
          </p>
          <p>
            El marketing directo ignora este miedo y presiona para cerrar. El marketing indirecto ataca este miedo de frente: educa al cliente. Le enseña en qué fijarse, qué preguntas hacerle a tus competidores, y por qué tu enfoque (tu protocolo o método) es superior. Cuando el cliente termina de educarse contigo, sus criterios de compra están moldeados a tu favor.
          </p>

          <h2 className="font-serif text-[28px] text-[#141414] mt-12 mb-6 tracking-[-0.01em]">La transición: De perseguir a ser perseguido</h2>
          <p>
            Cambiar de un modelo directo a uno indirecto requiere valentía y un cambio de mentalidad. Requiere aceptar que el clic no es una venta, y que la confianza tarda un poco más en fraguar. Pero los resultados a medio plazo son exponenciales:
          </p>
          <ul className="list-disc pl-6 space-y-3">
            <li><strong>Elasticidad de precio nula:</strong> Cuando un cliente viene educado por tu contenido y te ve como la autoridad máxima, el precio deja de ser el factor decisivo. Ya no compites por ser el más barato.</li>
            <li><strong>Ventas sin fricción:</strong> Tus comerciales (o tú mismo) dejaréis de rebatir objeciones constantemente. El cliente llega a la reunión casi "cerrado", solo buscando confirmación.</li>
            <li><strong>Previsibilidad real:</strong> El marketing indirecto, cuando se sistematiza mediante embudos de advertorials y publicidad nativa en canales como Taboola, se convierte en una máquina predecible de autoridad y demanda.</li>
          </ul>

          <h2 className="font-serif text-[28px] text-[#141414] mt-12 mb-6 tracking-[-0.01em]">Tu próximo movimiento</h2>
          <p>
            Revisa tus campañas actuales. Si todo lo que haces grita "compra ya" y sientes que estás persiguiendo a clientes que no te valoran, estás atrapado en la rueda de hámster del marketing directo obsoleto.
          </p>
          <p>
            Ha llegado el momento de dejar el megáfono y empezar a escribir el libro de tu sector. La autoridad es el único activo de marketing que no sufre inflación y que ningún competidor, por mucho dinero que tenga, te puede copiar.
          </p>

          <div className="mt-16 pt-10 border-t border-[#141414]/10">
            <div className="bg-[#141414] text-white p-8 md:p-12">
              <h3 className="font-serif text-[24px] md:text-[30px] mb-4">
                Construye un sistema basado en autoridad, no en presión
              </h3>
              <p className="text-white/70 mb-8 max-w-[600px]">
                Te ayudamos a hacer la transición del marketing de interrupción al marketing de educación y autoridad a través de la red de publicidad nativa más potente del mundo.
              </p>
              <Link 
                to="/#cta" 
                className="inline-flex items-center gap-2 bg-white text-[#141414] px-6 py-3 font-medium hover:bg-white/90 transition-colors"
                onClick={() => {
                  setTimeout(() => {
                    const el = document.querySelector('#cta');
                    if (el) el.scrollIntoView({ behavior: 'smooth' });
                  }, 100);
                }}
              >
                Hablemos de tu estrategia
              </Link>
            </div>
          </div>
        </article>
      </section>

      <Footer />
    </main>
  );
}
